import React, {
    createContext,
    useReducer,
    useEffect,
    useContext,
    useState,
} from "react";
import PropTypes from "prop-types";
import CartSession from "./cartSession";
import FbClientIdSession, { FB_URL_PARAM } from "./fbClientIdSession";
import {
    updateCartMutation,
    checkoutCartMutation,
    createCartMutation,
} from "~/assets/js/Api/cartApi";
import { generateUuid, getUrlQueryParam } from "~/assets/js/Helpers/util";
import {
    updateUser,
    loginUserWithAutoLoginCookie,
} from "~/assets/js/Api/userApi";
import { COUPON } from "~/assets/js/Helpers/coupons";
import { POTTSALAT_PAYMENT } from "~/assets/js/Helpers/paymentOptions";
import { initFbClientIdentity } from "~/assets/js/Api/eventsApi";
import { generateNewCartData } from "~/assets/js/Helpers/cookie-utils";
import { ITEM } from "../Helpers/items";
import MetaPixelUtil from "../Helpers/meta-pixel-util";
import GoogleTagUtil from "../Helpers/google-tag-util";
import * as Matomo from "../Helpers/matomo";
import { BOWLS_TYPES } from "~/assets/js/components/ReusableBowls/BowlsTypes";
import GroupOrderNotifications from "./GroupOrderNotification";

export const ACTIONS = {
    SET_CART: "SET_CART",
};

const CartContext = createContext(CartSession.initialState);

const CartReducer = (state, action) => {
    const { type, payload } = action;
    switch (type) {
        case ACTIONS.SET_CART:
            const discount = payload?.discount;
            const items = payload?.items;

            if (!discount) {
                return payload;
            }

            const withoutInvalidCoupons = discount?.coupons.filter(
                (coupon) => coupon.valid === COUPON.VALID
            );

            const withoutInvalidItems = items?.filter(
                (item) => item.valid === ITEM.VALID
            );

            payload.items = withoutInvalidItems;

            const didRemoveCoupons =
                discount.coupons.length > withoutInvalidCoupons.length;

            if (didRemoveCoupons) {
                discount.coupons = withoutInvalidCoupons;
                window.dispatchEvent(
                    new CustomEvent("discount-removed", {
                        bubbles: false,
                    })
                );
            }

            return { ...payload, discount };
        default:
            throw new Error();
    }
};

export const useCartContext = () => useContext(CartContext);

const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(CartReducer, CartSession.initialState);
    const [isLoading, setLoading] = useState(true);
    const [isError, setError] = useState(false);
    const requiresPayment = parseFloat(state?.value_gross || 0) > 0;
    const coupons = state?.discount?.coupons;
    const isTimeslotSelected = Boolean(state?.slot_id);
    const { reusableBowlsId } = reactConfig;
    const numberOfDiscountedProducts =
        state?.items?.reduce(
            (total, item) => total + item.discounted_by_membership,
            0
        ) || 0;

    useEffect(() => {
        init();
        initFbIdentity();
        loginUserWithAutoLoginCookie();
    }, []);

    useEffect(() => {
        CartSession.update(state);
    }, [state]);

    const initFbIdentity = async () => {
        const fbClientId = getUrlQueryParam(FB_URL_PARAM);
        const isFirstVisit = !FbClientIdSession.get()?.wasIdentified;

        //Fire request even if no fbClient id on first time visit.
        if (isFirstVisit) {
            initFbClientIdentity(fbClientId);
            FbClientIdSession.update({
                ...FbClientIdSession.get(),
                ...{ wasIdentified: true, fbClientId: fbClientId },
            });
        }

        const shouldReInitIdentity =
            fbClientId && !FbClientIdSession.isCurrent(fbClientId);

        if (shouldReInitIdentity) {
            initFbClientIdentity(fbClientId);
            FbClientIdSession.update({
                ...FbClientIdSession.get(),
                ...{ wasIdentified: true, fbClientId: fbClientId },
            });
        }
    };

    /**
     * Fetches current local instance (session) cart
     * otherwise inits a new instance of cart from the BE API
     *
     * @returns cart
     */
    const getCart = async () => {
        if (CartSession.isSet()) {
            const currentCartInSession = CartSession.get();
            return currentCartInSession;
        }

        // If there is no location set, do not initiate new cart -> will result in error
        if (!window.cookie.isSet()) {
            return;
        }

        const { cart: newCart } = await createCartMutation(
            generateNewCartData()
        );

        return newCart;
    };

    const updateCartState = async (cart) => {
        dispatch({ type: ACTIONS.SET_CART, payload: cart });
    };

    const init = async () => {
        setLoading(true);
        const cart = await getCart();
        updateCartState(cart);
        setLoading(false);
    };

    const updateOnCookieChange = async () => {
        setLoading(true);
        let cart;

        if (!CartSession.isSet()) {
            cart = await getCart();
        } else {
            cart = CartSession.get();
            const isGroupOrder = cart?.group_cart;
            if (!isGroupOrder) {
                const { cart: updatedCart } = await updateCartMutation({
                    ...cart,
                    ...generateNewCartData(),
                });
                cart = updatedCart;
            }
        }

        updateCartState(cart);
        setLoading(false);
    };

    const addConfigurableProductToCart = async (
        productId,
        quantity = 1,
        numberOfDiscountedItems = 0,
        add = undefined,
        remove = undefined,
        menuAddons = undefined
    ) => {
        setLoading(true);

        const cart = CartSession.get();
        const item_uid = generateUuid();
        const menu_uid = generateUuid();

        const newItem = {
            uid: item_uid,
            product_id: productId,
            quantity: quantity,
            discounted_by_membership: numberOfDiscountedItems,
            add: add,
            remove: remove,
            menu_uid,
        };

        const cookie = window.cookie.get();
        if (cookie?.participant) {
            newItem.owner_name = cookie.participant.name;
            newItem.owner_uid = cookie.participant.uid;
        }

        cart.items.push(newItem);
        if (menuAddons) {
            cart.items.push({
                uid: generateUuid(),
                product_id: reactConfig.baseMenuId, //TODO put into WP Config
                quantity,
                add: menuAddons.map((id) => {
                    return { product_id: id, quantity: 1 };
                }),
                menu_uid,
            });
        }

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);

            MetaPixelUtil.trackAddToCartEvent({
                content_type: "product",
                contents: [{ id: productId, quantity: quantity }],
            });

            const addedItems = updatedCart.cart.items.filter(
                (item) => item.product_id === productId
            );

            // if uid does not exist don't send an event (most likely because of an error or deduplication)
            if (!addedItems.length) return;
            const addedItem = addedItems[0];
            updatedCart.cart.items.forEach((item) => {
                Matomo.addToCart(
                    item.product_id,
                    item.name,
                    item.category,
                    item.value_gross,
                    item.quantity
                );
            });
            Matomo.updateCart(updatedCart.cart.value_gross);

            window.posthog?.capture("add to cart", {
                name: addedItem.name,
                price: parseFloat(addedItem.single_value_gross),
            });
            
            // TODO: values are not correct if users add the same product again
            GoogleTagUtil.trackAddToCartEvent({
                currency: "EUR",
                value: parseFloat(addedItem.value_gross),
                items: [
                    {
                        item_id: String(productId),
                        item_name: addedItem.name,
                        quantity: quantity,
                        price: parseFloat(addedItem.single_value_gross),
                    },
                ],
            });
            return;
        }

        setError(true);
        setLoading(false);
    };

    const addMultipleItemsToCart = async (items) => {
        const cart = CartSession.get();

        cart.items = items;

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            MetaPixelUtil.trackAddToCartEvent({
                content_type: "product",
                contents: updatedCart.cart.items.map((item) => {
                    return { id: item.product_id, quantity: item.quantity };
                }),
            });

            GoogleTagUtil.trackAddToCartEvent({
                currency: "EUR",
                value: parseFloat(updatedCart.cart.value_gross),
                items: updatedCart.cart.items.map((item) => {
                    return {
                        item_id: String(item.product_id),
                        item_name: item.name,
                        quantity: item.quantity,
                        price: parseFloat(item.single_value_gross),
                    };
                }),
            });
            return updatedCart;
        }

        setError(true);
        setLoading(false);
    };

    const addItemToCart = async (productId, quantity = 1) => {
        setLoading(true);

        const cart = CartSession.get();

        const newItem = {
            uid: generateUuid(),
            product_id: productId,
            quantity: quantity,
        };

        const cookie = window.cookie.get();
        if (cookie?.participant) {
            newItem.owner_name = cookie.participant.name;
            newItem.owner_uid = cookie.participant.uid;
        }

        if (cart.items.length > 0) {
            cart.items.some((item) => {
                if (item.product_id === productId) {
                    item.quantity = quantity;
                    return false;
                }

                cart.items.push(newItem);
                return true;
            });
        } else {
            cart.items.push(newItem);
        }

        const updatedCart = await updateCartMutation(cart);
        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);

            MetaPixelUtil.trackAddToCartEvent({
                content_type: "product",
                contents: [{ id: productId, quantity: quantity }],
            });
            // TODO: GoogleTag Add To cart

            return;
        }

        setError(true);
        setLoading(false);
    };

    const updateItemQuantity = async (inCartPosition, quantity = 1) => {
        setLoading(true);

        const cart = CartSession.get();

        if (cart.items[inCartPosition].menu_uid) {
            cart.items.forEach((item) => {
                if (item.menu_uid === cart.items[inCartPosition].menu_uid) {
                    item.quantity = quantity;
                }
            });
        } else {
            cart.items[inCartPosition].quantity = quantity;
        }

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const removeItemFromCart = async (inCartPosition) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.items.splice(inCartPosition, 1);

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const removeMultipleItemsFromCartById = async (ids) => {
        let actionResponse = { ok: false };
        setLoading(true);
        const cart = CartSession.get();

        const filteredItems = cart.items.filter(
            (item) => !ids.includes(item.product_id)
        );

        cart.items = filteredItems;
        const updatedCart = await updateCartMutation(cart);
        setLoading(false);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            actionResponse = { ok: true };
        }

        return actionResponse;
    };

    /**
     * @param {BOWLS_TYPES} result
     * @returns
     */
    const toggleReusableBowls = async (result) => {
        setLoading(true);
        const cart = CartSession.get();

        if (result === BOWLS_TYPES.REUSABLE) {
            const updatedCart = await updateCartMutation(cart);
            if (updatedCart.ok) updateCartState(updatedCart.cart);
        } else {
            await removeMultipleItemsFromCartById([reusableBowlsId]);
        }

        setLoading(false);
    };

    const updateFromPreviousOrder = async (previousOrderData) => {
        let actionResponse;
        setLoading(true);

        try {
            // Cart object is retrieved from session and filled with cookie data
            const cart = {
                ...CartSession.get(),
                ...generateNewCartData(),
            };

            // Update cart information with values from previous order
            let { billingAddress, email, phone, payment } = previousOrderData;

            // previous order was to private person thus we are removing the legal form
            const wasPrivatePerson = billingAddress.legal_form === null;

            if (wasPrivatePerson) {
                delete billingAddress.legal_form;
            }

            cart.billing = billingAddress;
            cart.email = email;
            cart.phone = phone;

            if (payment) {
                cart.payment.method = payment;
            }

            const updatedCart = await updateCartMutation(cart);

            if (!updatedCart.ok) {
                throw new Error();
            }

            updateCartState(updatedCart.cart);
            actionResponse = { ok: true, message: "Success" };
        } catch (error) {
            actionResponse = {
                ok: false,
                message:
                    "Action updateAddressData did not execute successfully",
            };
        }

        setLoading(false);
        return actionResponse;
    };
    const updateAddressData = async (data, shouldUpdateCustomer = true) => {
        let actionResponse;
        setLoading(true);

        try {
            const cart = CartSession.get();
            let {
                billingAddress,
                deliveryAddress,
                sameAsBilling,
                email,
                is_company,
                phone,
            } = data;

            const customerType = is_company ? "B2B" : "B2C";
            const cookie = window.cookie.get();

            if (sameAsBilling) {
                billingAddress = deliveryAddress;
            }

            cart.billing = { ...billingAddress, customer_type: customerType };
            cart.shipping = {
                ...deliveryAddress,
                customer_type: customerType,
                lon: String(cookie.location?.lon),
                lat: String(cookie.location?.lat),
            };

            cart.email = email;
            cart.phone = phone;

            // If pickup we need to remove any shipping information
            const isPickup = cookie.area.type === "pickup";
            if (isPickup) {
                delete cart.shipping;
            }

            const updatedCart = await updateCartMutation(cart);

            let updatedUser = { ok: true };

            if (shouldUpdateCustomer) {
                updatedUser = await updateUser({
                    name: billingAddress.first_name,
                    lastName: billingAddress.last_name,
                    email: email,
                });
            }

            if (!updatedCart.ok || !updatedUser.ok) {
                throw new Error();
            }

            updateCartState(updatedCart.cart);
            actionResponse = { ok: true, message: "Success" };
        } catch (error) {
            actionResponse = {
                ok: false,
                message:
                    "Action updateAddressData did not execute successfully",
            };
        }

        setLoading(false);

        return actionResponse;
    };

    const updatePaymentMethod = async (paymentMethod) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.payment = {
            method: paymentMethod?.value,
        };
        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const updateTimeSlot = async (timeslot) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.slot_begin = timeslot.begin;
        cart.slot_end = timeslot.end;
        cart.slot_id = timeslot.id;
        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return updatedCart;
        }

        setError(true);
        setLoading(false);
        return updatedCart;
    };

    const updateCutlery = async (cutlery) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.cutlery = cutlery;

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const updateCompensation = async (compensation) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.compensation.level = compensation;

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const updateEarlyOnLocation = async (earlyOnLocation) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.shipping = { ...cart.shipping, earlier_allowed: earlyOnLocation };

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const addCoupon = async (coupon) => {
        setLoading(true);
        const cart = CartSession.get();
        const existingCoupon = cart?.discount?.coupons?.find(
            (cartCoupon) => cartCoupon.code === coupon
        );

        if (!existingCoupon) {
            cart.discount.coupons.push({ code: coupon });
        }

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            const couponValid = updatedCart.cart.discount.coupons.filter(
                (updatedCoupon) => updatedCoupon.code === coupon
            );
            //remove invalid coupons
            updatedCart.cart.discount.coupons =
                updatedCart.cart.discount.coupons.filter(
                    (coupon) => coupon.valid === COUPON.VALID
                );
            setLoading(false);
            updateCartState(updatedCart.cart);
            return { valid: couponValid[0].valid };
        }

        setError(true);
        setLoading(false);
    };

    const removeCoupon = async (code) => {
        setLoading(true);
        const cart = CartSession.get();
        cart.discount.coupons = cart.discount.coupons.filter(
            (coupon) => coupon.code !== code
        );

        const updatedCart = await updateCartMutation(cart);

        if (updatedCart.ok) {
            updateCartState(updatedCart.cart);
            setLoading(false);
            return;
        }

        setError(true);
        setLoading(false);
    };

    const checkoutCart = async (deliveryInstructions, comment) => {
        setLoading(true);
        const cookie = window.cookie.get();

        const cart = CartSession.get();
        cart.note = comment;

        const isDelivery = cookie.area.type === "delivery";

        if (isDelivery) {
            cart.shipping = { ...cart.shipping, ...deliveryInstructions };
        } else {
            delete cart.shipping;
        }

        if (!requiresPayment) {
            cart.payment.method = POTTSALAT_PAYMENT.NONE;
        }

        const updatedCart = await checkoutCartMutation(cart);

        if (updatedCart.cart) {
            updateCartState(updatedCart.cart);
        }

        if (updatedCart.ok) {
            //Update cookie with last submitted order.
            window.cookie.update({
                ...window.cookie.get(),
                lastOrder: {
                    cartUid: cart.uid,
                    requestUid: cart.request_uid,
                },
            });

            setLoading(false);
            return updatedCart;
        }

        setLoading(false);
        setError(true);
        return updatedCart;
    };

    return (
        <CartContext.Provider
            value={{
                cart: state,
                cartItems: state?.items || [],
                numberOfDiscountedProducts,
                membershipDiscount: state?.discount?.value_gross_membership,
                requiresPayment,
                coupons,
                isTimeslotSelected,
                actions: {
                    getCart,
                    updateCartState,
                    updateOnCookieChange,
                    addConfigurableProductToCart,
                    addMultipleItemsToCart,
                    addItemToCart,
                    updateItemQuantity,
                    removeItemFromCart,
                    removeMultipleItemsFromCartById,
                    updateAddressData,
                    updatePaymentMethod,
                    updateTimeSlot,
                    updateCutlery,
                    updateEarlyOnLocation,
                    updateCompensation,
                    addCoupon,
                    removeCoupon,
                    checkoutCart,
                    updateFromPreviousOrder,
                    toggleReusableBowls,
                },
                cartLoading: isLoading,
                cartError: isError,
                errorTypes: state?.errors,
            }}>
            <GroupOrderNotifications>{children}</GroupOrderNotifications>
        </CartContext.Provider>
    );
};

CartProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};

export { CartProvider, CartContext };
